import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Global } from '../types/global.type';

export const EMAIL_REGEXP =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
export const TEAMS_CHANNEL_REGEXP = /^#[a-zA-Z0-9_-]+$/;

export class FormHelper {
  static disableFormControls(group: FormGroup): void {
    Object.values(group.controls).forEach(control => control.disable());
  }

  static enableFormControls(group: FormGroup): void {
    Object.values(group.controls).forEach(control => control.enable());
  }

  static isFieldInvalid(form: FormGroup, fieldName: string, validations: string[] = null): boolean {
    const control = form.get(fieldName);
    return this.isControlInvalid(control, validations);
  }

  static isArrayFieldInvalid(form: FormArray, index: number, field: string, validations: string[] = null): boolean {
    const control = form.at(index)?.get(field);
    return this.isControlInvalid(control, validations);
  }

  static isControlInvalid(control: AbstractControl, validations: string[] = null): boolean {
    if (!control) {
      return false;
    }

    if (validations) {
      return validations.some(validation => control.hasError(validation) && (control.touched || control.dirty));
    }

    return control.invalid && (control.touched || control.dirty);
  }

  static getFieldErrors(form: FormGroup, field: string): string[] {
    const control = form.get(field);
    return control ? Object.keys(control.errors || {}) : [];
  }

  static validateAllFormFields(formGroup: FormGroup | FormArray): void {
    if (!formGroup) return;

    formGroup.markAsTouched();
    formGroup.markAsDirty();

    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormControl) {
        control.markAsTouched();
        control.markAsDirty();
      } else if (control instanceof FormArray) {
        control.markAsTouched();
        control.markAsDirty();
        control.controls.forEach(ctrl => {
          if (ctrl instanceof FormControl) {
            ctrl.markAsTouched();
            ctrl.markAsDirty();
          } else if (ctrl instanceof FormGroup || ctrl instanceof FormArray) {
            this.validateAllFormFields(ctrl);
          }
        });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  static findInvalidControlsNames(formGroup: FormGroup | FormArray): string[] {
    return Object.keys(formGroup.controls).filter(field => formGroup.get(field)?.invalid);
  }

  static setEnabled(control: AbstractControl, enabled: boolean, emitEvent: boolean = null): void {
    if (enabled) {
      control.enable({ emitEvent });
    } else {
      control.disable({ emitEvent });
    }
  }

  static setEnabledAndDefaultValues(controlsToUpdate: { control: AbstractControl; shouldBeEnabled: boolean; defaultValue?: any }[]): void {
    controlsToUpdate.forEach(({ control, shouldBeEnabled, defaultValue }) => {
      if (shouldBeEnabled) {
        if (control.disabled && defaultValue != null) {
          control.setValue(defaultValue);
        }
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  static setFocusInputSelectElement(elementName: string, shouldSelect?: boolean): void {
    setTimeout(() => {
      const element = document.getElementsByName(elementName)[0];
      if (!element) return;

      const input =
        element instanceof HTMLInputElement || element instanceof HTMLSelectElement
          ? element
          : (element.querySelector('input') ?? element.querySelector('select'));
      if (input) {
        input.focus();
        if (shouldSelect && input instanceof HTMLInputElement) input.select();
      }
    }, 0);
  }

  static resetAndDisable(control: AbstractControl | AbstractControl[]): void {
    const controlArray = Array.isArray(control) ? control : [control];
    controlArray.forEach(ctrl => {
      ctrl.reset();
      ctrl.disable();
    });
  }

  static clearWithoutValidation(control: AbstractControl): void {
    control.setValue(null, { emitEvent: false });
    control.clearValidators();
    control.updateValueAndValidity();
  }

  static setAndEnable(control: AbstractControl | AbstractControl[], value: any): void {
    const controlArray = Array.isArray(control) ? control : [control];
    controlArray.forEach(ctrl => {
      ctrl.setValue(value);
      ctrl.enable();
    });
  }

  public static initializeControl<T>(
    name: string,
    shouldBeVisible: boolean,
    getControl: () => AbstractControl<T>,
    form: FormGroup,
    formSubscriptions: Map<string, Subscription>,
    initializeControls: () => Promise<void>,
    initializeOnChange = false,
    shouldBeEnabled = true,
    valueChanged?: (v: any) => void,
  ): void {
    const controlExists = !!form.controls[name];

    if (shouldBeVisible && !controlExists) {
      const control = getControl();
      form.addControl(name, control);

      if (initializeOnChange || valueChanged) {
        const subscription = control.valueChanges.subscribe(value => {
          valueChanged?.(value);
          if (initializeOnChange) void initializeControls();
        });
        formSubscriptions.set(name, subscription);
      }
    } else if (!shouldBeVisible && controlExists) {
      form.removeControl(name);
      formSubscriptions.get(name)?.unsubscribe();
    }

    const control = form.get(name);
    if (!control) return;

    if (shouldBeEnabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  // Validators
  public static atLeastOneTrueValidator(control: AbstractControl): ValidationErrors {
    const valid = (control as FormArray).controls.some(c => c.value === true);
    return valid ? null : { atLeastOneError: true };
  }

  public static emptyOrUniqueValidator(control: AbstractControl): ValidationErrors {
    const array = control.value as string[];
    const valid = array.length === 0 || array.length === new Set(array).size;
    return valid ? null : { emptyOrUnique: true };
  }

  public static onceDateTimeValidator(control: AbstractControl): ValidationErrors {
    const valid = control.value > new Date();
    return valid ? null : { onceDateTime: true };
  }

  public static parametersGroupValidator(control: AbstractControl): ValidationErrors {
    const { name, value } = (control as FormGroup).controls;
    const valid = (!name.value && !value.value) || (name.value && value.value);
    return valid ? null : { parametersGroup: true };
  }

  public static emptyIdValidator(control: AbstractControl): ValidationErrors {
    const valid = control.value && control.value !== Global.defaultGUID;
    return valid ? null : { emptyId: true };
  }

  public static mustExistValidator(valuesGetter: () => { id: string }[], idGetter?: (value: any) => any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const values = valuesGetter();
      const value = idGetter ? idGetter(control.value) : control.value;
      const valid = values?.some(v => v.id === value);
      return valid ? null : { mustExist: true };
    };
  }

  public static uriComponentValidator(control: AbstractControl): ValidationErrors {
    const valid = !control.value || control.value === encodeURIComponent(control.value);
    return valid ? null : { uriComponent: true };
  }

  public static greaterThanValidator(field: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const group = control.parent;
      const fieldToCompare = group?.get(field);
      const valid = fieldToCompare && Number(fieldToCompare.value) < Number(control.value);
      return valid ? null : { greaterThanError: true };
    };
  }

  public static daysValidator(control: AbstractControl): ValidationErrors {
    const array = (control.value as string)
      .split(',')
      .map(v => v.trim())
      .filter(v => v !== '')
      .map(v => (/^-?\d+$/.exec(v) ? parseInt(v) : NaN));
    const valid = array.every(v => !isNaN(v) && v >= -31 && v <= 31 && v !== 0) && array.length > 0;
    return valid ? null : { days: true };
  }

  public static multipleEmailsValidator(control: AbstractControl, isTeamsChannelSupported = false): ValidationErrors {
    if (control.value == null || control.value === '') {
      return null;
    }

    const emails = (control.value as string).split(',').map(e => e.trim());
    const valid = emails.every(e => {
      if (e.length === 0) return true;
      if (EMAIL_REGEXP.test(e)) return true;
      if (isTeamsChannelSupported && TEAMS_CHANNEL_REGEXP.test(e)) return true;
      return false;
    });
    return valid ? null : { email: true };
  }

  public static noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const isWhitespace = ((control.value as string) || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  public static uniqueValidator(getValues: () => string[], getExcludedValue: () => string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const excludedValue = getExcludedValue();
      const allValues = getValues() ?? [];
      const currentValue = (control.value as string)?.trim();
      const valid = allValues.every(v => v !== currentValue || v === excludedValue);
      return valid ? null : { isUnique: true };
    };
  }

  public static timeWindowValidator(control: AbstractControl): ValidationErrors | null {
    const { from, to } = (control as FormGroup).controls;
    const invalid = from.value > to.value;
    return invalid ? { timeWindow: true } : null;
  }

  public static debugInvalidFields(form: FormGroup | FormArray, label = 'Form'): void {
    if (environment.production) return;
    console.group(`${label} validation failed`);
    const log = (group: AbstractControl, path: string): void => {
      if (group instanceof FormGroup || group instanceof FormArray) {
        Object.entries(group.controls).forEach(([key, control]) => {
          const controlPath = `${path}.${key}`;
          if (control.invalid) {
            console.warn(`${controlPath}:`, control.errors, 'value:', control.value);
          }
          log(control, controlPath);
        });
      }
    };
    log(form, label);
    console.groupEnd();
  }

  // for testing purposes
  public static getFormInvalidFields(form: FormGroup): string[] {
    return Object.keys(form.controls).filter(key => form.get(key).invalid);
  }

  public static focusFirstInput(): void {
    const firstInput = document.querySelector('input:not([disabled]):not([readonly])');
    if (firstInput instanceof HTMLInputElement && firstInput.offsetParent !== null) {
      firstInput.focus();
    }
  }
}

export function uniqueItemInArrayValidator(validationArray: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || !validationArray || validationArray.length === 0) {
      return null;
    }

    const valid = !validationArray.includes(control.value as string);
    return valid ? null : { emptyOrUnique: true };
  };
}
